"use client";

/**
 * Small form pieces shared by /login and /setup: a labelled pill field with an
 * inline error, a password input with a show/hide button, a form-level alert
 * and a full-page status spinner.
 */
import { useState, type ComponentProps, type ReactNode, type Ref } from "react";
import { AlertTriangle, Eye, EyeOff, Info, Loader2, type LucideIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface FieldProps {
  id: string;
  className?: string;
  label: string;
  /** red "*" before the label (form system); /login does not pass it */
  required?: boolean;
  error?: string | null;
  help?: string | null;
  children: (describedBy: string | undefined, invalid: boolean) => ReactNode;
}

/** Label + control + help/error text, wired with aria-describedby. */
export function Field({ id, label, required, error, help, children, className }: FieldProps) {
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;
  const describedBy = [error ? errorId : null, help ? helpId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {required ? (
          <span aria-hidden="true" className="me-0.5 font-semibold text-destructive">
            *
          </span>
        ) : null}
        {label}
      </label>
      {children(describedBy, !!error)}
      {error ? (
        <p id={errorId} className="flex items-start gap-1.5 text-[13px] leading-5 text-destructive">
          <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" strokeWidth={2} />
          <span>{error}</span>
        </p>
      ) : null}
      {help ? (
        <p id={helpId} className="text-xs leading-5 text-muted-foreground">
          {help}
        </p>
      ) : null}
    </div>
  );
}

export interface PasswordInputProps extends Omit<ComponentProps<"input">, "type"> {
  showLabel: string;
  hideLabel: string;
  inputRef?: Ref<HTMLInputElement>;
  /** Controlled visibility (the sign-in characters react to it); uncontrolled when omitted. */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
}

/** Pill input with an eye button at the end that toggles visibility. */
export function PasswordInput({
  showLabel,
  hideLabel,
  inputRef,
  className,
  visible: visibleProp,
  onVisibleChange,
  ...props
}: PasswordInputProps) {
  const [visibleState, setVisibleState] = useState(false);
  const visible = visibleProp ?? visibleState;
  const setVisible = (next: boolean) => {
    if (visibleProp === undefined) setVisibleState(next);
    onVisibleChange?.(next);
  };
  return (
    <div className="relative">
      <Input
        {...props}
        ref={inputRef}
        type={visible ? "text" : "password"}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className={cn("pe-12", className)}
      />
      <button
        type="button"
        aria-label={visible ? hideLabel : showLabel}
        aria-pressed={visible}
        aria-controls={props.id}
        onClick={() => setVisible(!visible)}
        disabled={props.disabled}
        className="absolute end-0 top-0 inline-flex size-11 items-center justify-center rounded-pill text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none disabled:opacity-50"
      >
        {visible ? (
          <EyeOff aria-hidden="true" className="size-5" strokeWidth={1.8} />
        ) : (
          <Eye aria-hidden="true" className="size-5" strokeWidth={1.8} />
        )}
      </button>
    </div>
  );
}

/** Form-level message box. `tone` error = red pill colours, warning = amber tint, info = indigo tint. */
export function FormNotice({
  tone,
  title,
  children,
  id,
  live = true,
  icon,
}: {
  tone: "error" | "warning" | "info";
  title?: string;
  children: ReactNode;
  id?: string;
  live?: boolean;
  icon?: LucideIcon;
}) {
  const Icon = icon ?? (tone === "info" ? Info : AlertTriangle);
  return (
    <div
      id={id}
      role={live ? (tone === "error" ? "alert" : "status") : undefined}
      className={cn(
        "flex items-start gap-2.5 rounded-card border px-3.5 py-3 text-[13px] leading-relaxed",
        tone === "error" && "border-pill-red-border bg-pill-red text-pill-red-foreground",
        tone === "warning" && "border-tint-assign-border bg-tint-assign text-tint-assign-foreground",
        tone === "info" && "border-tint-info-border bg-tint-info text-tint-info-foreground",
      )}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-[18px] shrink-0" strokeWidth={1.8} />
      <div className="flex min-w-0 flex-col gap-0.5">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div>{children}</div>
      </div>
    </div>
  );
}

/** Full-height centred spinner with a label (session checks, redirects). */
export function PageStatus({ label }: { label: string }) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-background px-4 pt-safe pb-safe">
      <div role="status" aria-live="polite" className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 aria-hidden="true" className="size-6 animate-spin text-brand-text" />
        <span className="text-sm">{label}</span>
      </div>
    </main>
  );
}
